import { z } from 'zod';
import type { SourceGrade } from './contratto';
import type { StepTimelineDto } from './onboarding';

/**
 * La fase 0 «Conosco l'azienda» del contratto n. 1 versione 2.0 (ADR 0171): le domande dell'art. 7-bis, il livello
 * dell'art. 9.3 e la pagina della fase (profilo, atto sezione per sezione, conti in tre gradini, contraddizioni).
 */

/** Il peso di una domanda (art. 7-bis.5): prima quelle che cambiano un obbligo, poi un numero, poi le altre. */
export const QUESTION_WEIGHTS = ['obbligo', 'numero', 'altro'] as const;
export type QuestionWeight = (typeof QUESTION_WEIGHTS)[number];

/** Quante domande al massimo: 5 per fase, 30 per incarico (art. 7-bis.5). */
export const MAX_QUESTIONS_PER_PHASE = 5;
export const MAX_QUESTIONS_PER_ENGAGEMENT = 30;

/** La chiave della risposta sempre disponibile «Basta, decidi tu» (art. 3.101). */
export const DECIDE_KEY = 'decidi';
/** La chiave della risposta «Altro», con uno spazio libero (art. 7-bis.4). */
export const OTHER_KEY = 'altro';

/** Una risposta possibile, con l'effetto nel progetto e, se è quella che Timonio adotta senza risposta, il segno. */
export interface PhaseAnswerOptionDto {
  chiave: string;
  testo: string;
  /** Cosa cambia nel progetto con questa risposta, accanto (art. 7-bis.4). */
  effetto: string | null;
  /** La risposta che Timonio adotta se il titolare non risponde, già evidenziata. */
  predefinita: boolean;
  /** I campi liberi che la risposta chiede (es. «Via, numero, comune»; «A Bari», «A Modugno»). */
  campi?: string[];
  /** Il valore che la voce da cui nasce la domanda prende con questa risposta (art. 7-bis.6); null = il testo della risposta. */
  valore?: string | null;
}

/** I cinque stati di una domanda del registro (art. 3.140). */
export const QUESTION_STATES = ['aperta', 'scelta_timonio', 'risposta', 'superata', 'non_chiesta'] as const;
export type QuestionState = (typeof QUESTION_STATES)[number];
/** Come si legge lo stato nella pagina. */
export const QUESTION_STATE_TEXT: Record<QuestionState, string> = {
  aperta: 'Aperta',
  scelta_timonio: 'Scelta di Timonio',
  risposta: 'Risposta del titolare',
  superata: 'Superata',
  non_chiesta: 'Non chiesta',
};

export interface PhaseQuestionDto {
  id: string;
  /** La fase in cui nasce (0-11). */
  fase: number;
  /** La chiave stabile, per riconoscerla tra un giro e l'altro. */
  chiave: string;
  /** La voce da cui nasce, come la legge il titolare (es. «Unità locale n. 2, Modugno»). */
  voce: string;
  /** La voce da cui nasce, come la riconosce lo studio (`fase<N>:<id>:<campo>`); null per le domande scritte prima del registro. */
  voce_rif: string | null;
  /** Cosa cambia e in quali fasi, in parole (es. «cambia un dato della fase 2»). */
  cambia: string;
  fasi_cambiate: number[];
  domanda: string;
  /** Per una contraddizione: ciò che le fonti dicono, riletto e confermato. */
  spiegazione: string | null;
  /** Il perché della risposta predefinita, in una riga: perché è la più probabile per questa azienda (art. 7-bis.4, 3.132). */
  perche_predefinita: string | null;
  /** La fonte da cui il perché si ricava (il fascicolo di settore, i conti, le pagine verificate, la visura). */
  fonte_predefinita: string | null;
  /** Un esempio in una riga, scritto per questa azienda, quando una parola può non essere chiara; null se non serve. */
  esempio: string | null;
  risposte: PhaseAnswerOptionDto[];
  /** Si può scegliere più di una risposta (es. i contratti collettivi applicati, fase 1). */
  multipla: boolean;
  peso: QuestionWeight;
  /** Uno dei cinque stati dell'art. 3.140. */
  stato: QuestionState;
  /** Nasce da una contraddizione tra fonti dello stesso grado (art. 7-bis.3). */
  contraddizione: boolean;
  /** La risposta, con la data; per una scelta di Timonio la risposta predefinita adottata. */
  risposta: { chiave: string; chiavi?: string[]; testo: string | null; valori?: string[]; il: string } | null;
  /** T quando ha risposto il titolare; null per una scelta di Timonio o senza risposta (art. 3.134-3.135). */
  grado: 'T' | null;
  /** La fase che adotta la risposta predefinita se il titolare non risponde (art. 7-bis.2); null = resta aperta. */
  adottata_in: number | null;
  /** Perché è superata: la fase o la fonte che l'ha risolta (art. 7-bis.5). */
  superata_perche: string | null;
}

/** Il riassunto del registro che ogni pagina di fase mostra (art. 7-bis.11). */
export interface QuestionRegistrySummaryDto {
  aperte: number;
  scelte_timonio: number;
  risposte: number;
  superate: number;
  non_chieste: number;
  /** Le aperte e le scelte di Timonio per fase, per la riga «1 della fase 0, 2 di questa fase». */
  per_fase: Array<{ fase: number; aperte: number; scelte_timonio: number }>;
}

export interface PhaseQuestionsDto {
  domande: PhaseQuestionDto[];
  /** Quante ne restano nell'incarico (30 in tutto). */
  limiti: { per_fase: number; per_incarico: number; usate_incarico: number };
  /** Il riassunto del registro intero (art. 7-bis.11). */
  registro: QuestionRegistrySummaryDto;
}

/** La risposta del titolare a una domanda di fase: la chiave della risposta scelta, con il testo libero se ne ha. */
export const PhaseAnswerSchema = z.object({
  chiave: z.string().trim().min(1).max(80),
  /** Le risposte scelte quando la domanda ne ammette più di una. */
  chiavi: z.array(z.string().trim().min(1).max(80)).max(10).optional(),
  testo: z.string().trim().max(2000).optional(),
  valori: z.array(z.string().trim().max(400)).max(10).optional(),
});
export type PhaseAnswerInput = z.infer<typeof PhaseAnswerSchema>;

/** «Non va bene», con il motivo: la fase si rifà una volta (art. 7-bis.8). */
export const PhaseRedoSchema = z.object({ motivo: z.string().trim().min(1).max(2000) });
export type PhaseRedoInput = z.infer<typeof PhaseRedoSchema>;

/** Il livello di risultato di una fase (art. 9.3), calcolato dallo studio. */
export const LEVEL_NAMES: Record<1 | 2 | 3 | 4 | 5, string> = { 1: 'Bozza', 2: 'Riconosciuta', 3: 'Completa', 4: 'Collegata', 5: 'Confermata' };

export interface PhaseLevelDto {
  valore: 1 | 2 | 3 | 4 | 5;
  nome: string;
  /** Il passo che manca per salire, in parole; null al livello 5. */
  prossimo: string | null;
  /** Il livello 4 si calcola dopo la fase 8: finché non c'è, la pagina lo dice. */
  nota: string | null;
}

/** Una voce dell'atto in forma leggibile, con la citazione esatta a parte. */
export interface PhaseZeroItemDto {
  campo: string;
  valore: string;
  citazione: string;
  /** Il dato manca o non basta: diventa una domanda. */
  da_confermare: boolean;
}

export interface PhaseZeroSectionDto {
  chiave: string;
  titolo: string;
  /** Quante voci, o cosa c'è da sapere («1 da confermare», «1 contraddizione», «3 cariche»). */
  nota: string;
  tono: 'normale' | 'attenzione';
  voci: PhaseZeroItemDto[];
  /** Il «perché?» della sezione: la fonte, il grado e la citazione, o cosa lo studio ha fatto (art. 7-bis.8). */
  perche: string | null;
  /** Una contraddizione dentro la visura, riletta e non corretta (art. 5.4, 7-bis.3). */
  contraddizione: string | null;
}

/** Il bilancio in tre gradini (art. 5.4), in parole semplici. */
export interface PhaseZeroStepDto {
  n: 1 | 2 | 3;
  titolo: string;
  testo: string;
  stato: 'fatto' | 'in_corso' | 'da_confermare' | 'saltato';
}

export interface PhaseZeroDto {
  /** in_preparazione: la visura si scarica o si legge; pronta; senza_visura: il Registro non l'ha data. */
  stato: 'in_preparazione' | 'pronta' | 'senza_visura';
  /** Il nome dell'azienda come lo dice il titolare: «Boostar». */
  azienda: string | null;
  /** La fonte dell'atto per nome: «Visura camerale del 20/10/2026». */
  fonte: string | null;
  grado: SourceGrade;
  profilo: {
    stato: 'assente' | 'in_preparazione' | 'pronto';
    settore: string | null;
    dimensione: string | null;
    territorio: string | null;
    /** Ciò che non torna tra le fonti, segnalato dal consulente. */
    non_torna: string[];
  };
  /** I bloccanti della fase, controllati dallo studio (art. 8, fase 0): unità locali N/N, poteri, addetti, oggetto, somme. */
  bloccanti: Array<{ cosa: string; ok: boolean; dettaglio: string }>;
  sezioni: PhaseZeroSectionDto[];
  conti: { gradini: PhaseZeroStepDto[] };
  domande: PhaseQuestionDto[];
  limiti: PhaseQuestionsDto['limiti'];
  livello: PhaseLevelDto | null;
  /** Il lettore e il contatore della fase (art. 10.4). */
  tempi: StepTimelineDto | null;
  /** «Non va bene» già usato: la fase si rifà una sola volta. */
  rifatta: boolean;
  /** Cosa è cambiato dopo «Non va bene» o una risposta, in parole. */
  cambiato: string[];
  /** La data di «Confermo la fase» (art. 4.1, 3.138), se il titolare ha confermato. */
  confermata: string | null;
}
